import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../services/API'
import { toDateInput } from '../kit/helpers'

// Sets where one course version stands with CPD UK. `version` is a row of
// GET /admincrm/training/courses (null = closed).
const AccreditationDialog = ({ version, statuses, onClose, onSaved }) => {
  const [form, setForm] = useState({ status: 'none', cpdReference: '', accreditedOn: '', cpdHours: {}, notes: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!version) return
    const current = version.accreditation ?? {}
    setForm({
      status: current.status ?? 'none',
      cpdReference: current.cpdReference ?? '',
      accreditedOn: toDateInput(current.accreditedOn),
      cpdHours: Object.fromEntries(
        Object.entries(current.cpdHours ?? {}).map(([pathwayId, hours]) => [pathwayId, String(hours)]),
      ),
      notes: current.notes ?? '',
    })
    setError(null)
  }, [version])

  const pathways = version?.course?.pathways ?? []
  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }))
  const referenceMissing = form.status === 'accredited' && !form.cpdReference.trim()

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const res = await API.put(
      `/admincrm/training/accreditation/${encodeURIComponent(version.code)}/${encodeURIComponent(version.version)}`,
      {
        status: form.status,
        cpdReference: form.cpdReference.trim(),
        accreditedOn: form.accreditedOn || null,
        cpdHours: form.cpdHours,
        notes: form.notes,
      },
    )
    setSaving(false)
    if (res.status === 200) onSaved?.(res.data)
    else setError(res.data?.error ?? 'Unable to save the accreditation record')
  }

  return (
    <Dialog open={!!version} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Accreditation: {version?.code} v{version?.version}
        <Typography component="span" display="block" variant="body2" color="text.secondary">
          {version?.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} paddingTop={1}>
          <Alert severity="info">
            Certificates and the feedback form only show the CPD Certified statement while the
            status is Accredited.
          </Alert>
          <TextField select label="Status" value={form.status} onChange={setField('status')} fullWidth>
            {statuses.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="CPD UK reference"
            value={form.cpdReference}
            onChange={setField('cpdReference')}
            required={form.status === 'accredited'}
            error={referenceMissing}
            helperText={referenceMissing ? 'Needed before a course can be marked as accredited' : undefined}
            fullWidth
          />
          <TextField
            label="Accredited on"
            type="date"
            value={form.accreditedOn}
            onChange={setField('accreditedOn')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {pathways.length > 0 && (
            <Stack gap={1}>
              <Typography variant="body2" fontWeight={600}>
                CPD hours per pathway
              </Typography>
              {pathways.map((pathway) => (
                <TextField
                  key={pathway.id}
                  label={pathway.title}
                  type="number"
                  size="small"
                  value={form.cpdHours[pathway.id] ?? ''}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      cpdHours: { ...prev.cpdHours, [pathway.id]: event.target.value },
                    }))
                  }
                  inputProps={{ min: 0, step: 0.25 }}
                  helperText={
                    pathway.estimatedMinutes ? `Estimated learning time: ${pathway.estimatedMinutes} minutes` : undefined
                  }
                  fullWidth
                />
              ))}
            </Stack>
          )}
          <TextField label="Notes" value={form.notes} onChange={setField('notes')} multiline minRows={2} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || referenceMissing}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AccreditationDialog
